import { useCallback, useEffect, useMemo, useState } from 'react';
import { type CartItemReference } from '../models/types/cart-item-reference';

const SHOW_ONLY_SELECTED_ITEM_LIMIT = 1;

export const useExpandableCartItems = (items: CartItemReference[], showOnlySelected?: boolean, enabled = true) => {
    const canCollapse = useMemo(() => {
        if (!enabled) return false;

        return showOnlySelected && items.length > SHOW_ONLY_SELECTED_ITEM_LIMIT;
    }, [items.length, showOnlySelected, enabled]);
    // If cart item expandable => Show limit item, otherwise show all item
    const [isExpanded, setIsExpanded] = useState(() => !canCollapse || !enabled);

    const visibleItems = useMemo(
        () => (isExpanded ? items : items.slice(0, SHOW_ONLY_SELECTED_ITEM_LIMIT)),
        [isExpanded, items]
    );

    const toggleExpanded = useCallback(() => {
        if (!enabled) return;

        setIsExpanded(prev => !prev);
    }, [enabled]);

    useEffect(() => {
        if (!canCollapse || !enabled) {
            setIsExpanded(true);
        }
    }, [canCollapse, enabled]);

    const sortedItems = useMemo(() => {
        return [...visibleItems].sort(
            (a, b) => new Date(a.createdAt ?? '').getTime() - new Date(b.createdAt ?? '').getTime()
        );
    }, [visibleItems]);

    return {
        toggleExpanded,
        enrichedCartItemsRendered: sortedItems,
        isExpanded,
        limitVisible: SHOW_ONLY_SELECTED_ITEM_LIMIT,
        isCartItemsExpandable: canCollapse,
    };
};
